import React from "react";
import {
  AlertTriangleIcon,
  AnchorIcon,
  BankIcon,
  BuildingIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  ClockIcon,
  DatabaseIcon,
  FileIcon,
  GlobeIcon,
  SyncIcon,
  TrendUpIcon,
  UsersIcon,
} from "../../Components/Icons";
import "./DataIntegration.css";

/** Pieces shared by the three Data Integration pages. */

const STAT_ICONS = {
  database: DatabaseIcon,
  check: CheckCircleIcon,
  sync: SyncIcon,
  trend: TrendUpIcon,
  alert: AlertTriangleIcon,
  file: FileIcon,
  clock: ClockIcon,
  refresh: SyncIcon,
};

export const SOURCE_ICONS = {
  bank: BankIcon,
  anchor: AnchorIcon,
  users: UsersIcon,
  building: BuildingIcon,
  file: FileIcon,
  globe: GlobeIcon,
};

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/** Chip modifier from a label: "In Progress" -> "chip chip--in-progress". */
export const chipClass = (value) =>
  `chip chip--${value.toLowerCase().replace(/[\s/&]+/g, "-")}`;

export function StatCards({ cards }) {
  return (
    <section className="row g-3" aria-label="Summary">
      {cards.map((card) => {
        const Icon = STAT_ICONS[card.icon] ?? DatabaseIcon;
        return (
          <div className="col-6 col-lg-4 col-xxl" key={card.key}>
            <article className="di-stat h-100">
              <span className={`kpi__icon kpi__icon--${card.tone}`}>
                <Icon size={22} />
              </span>
              <div className="di-stat__meta">
                <p className="kpi__label">{card.label}</p>
                <p className="di-stat__value">{card.value}</p>
                <p
                  className={`di-stat__foot${
                    card.footTone ? ` di-stat__foot--${card.footTone}` : ""
                  }`}
                >
                  {card.foot}
                </p>
              </div>
            </article>
          </div>
        );
      })}
    </section>
  );
}

/** Pagination footer shared by the integration tables. */
export function Pager({ page, setPage, perPage, setPerPage, total, noun, shown }) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pages = Array.from({ length: Math.min(totalPages, 4) }, (_, i) => i + 1);

  return (
    <div className="listbar listbar--foot">
      <p className="listbar__count">
        Showing 1 to {shown} of {total} {noun}
      </p>
      <div className="listbar__pager">
        <span className="select">
          <select
            value={perPage}
            aria-label="Rows per page"
            onChange={(event) => {
              setPerPage(Number(event.target.value));
              setPage(1);
            }}
          >
            {PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} per page
              </option>
            ))}
          </select>
          <ChevronDownIcon size={16} />
        </span>

        <nav className="pager" aria-label="Pagination">
          <button
            type="button"
            className="pager__btn"
            aria-label="First page"
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            <ChevronsLeftIcon size={16} />
          </button>
          <button
            type="button"
            className="pager__btn"
            aria-label="Previous page"
            disabled={page === 1}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          >
            <ChevronLeftIcon size={16} />
          </button>
          {pages.map((entry) => (
            <button
              key={entry}
              type="button"
              className={`pager__btn${
                entry === page ? " pager__btn--active" : ""
              }`}
              aria-current={entry === page ? "page" : undefined}
              onClick={() => setPage(entry)}
            >
              {entry}
            </button>
          ))}
          <button
            type="button"
            className="pager__btn"
            aria-label="Next page"
            disabled={page >= totalPages}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          >
            <ChevronRightIcon size={16} />
          </button>
          <button
            type="button"
            className="pager__btn"
            aria-label="Last page"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
          >
            <ChevronsRightIcon size={16} />
          </button>
        </nav>
      </div>
    </div>
  );
}
